import { EDGE_CLASSIFICATIONS } from "./gpsReplayGeometry";

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}

function coordinateAt(left, right, ratio) {
  return Object.freeze({
    latitude: left.latitude + (right.latitude - left.latitude) * ratio,
    longitude: left.longitude + (right.longitude - left.longitude) * ratio,
  });
}

function coordinateOnMatchedPath(points, timestampMs) {
  if (!Array.isArray(points) || points.length < 2) return null;
  if (timestampMs <= points[0].recordedAt.getTime()) return points[0].coordinates;
  if (timestampMs >= points.at(-1).recordedAt.getTime()) return points.at(-1).coordinates;
  for (let index = 0; index < points.length - 1; index += 1) {
    const leftMs = points[index].recordedAt.getTime();
    const rightMs = points[index + 1].recordedAt.getTime();
    if (timestampMs <= rightMs) {
      const ratio = rightMs > leftMs ? (timestampMs - leftMs) / (rightMs - leftMs) : 0;
      return coordinateAt(points[index].coordinates, points[index + 1].coordinates, clamp(ratio, 0, 1));
    }
  }
  return null;
}

function observationIndexAt(observations, timestampMs) {
  let low = 0;
  let high = observations.length - 1;
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    if (observations[middle].recordedAt.getTime() <= timestampMs) low = middle + 1;
    else high = middle - 1;
  }
  return Math.max(0, high);
}

export function metadataForObservation(observation) {
  const accuracyMeters = Number.isFinite(observation?.accuracyMeters) && observation.accuracyMeters > 0
    ? observation.accuracyMeters
    : null;
  const satelliteCount = Number.isSafeInteger(observation?.satelliteCount) && observation.satelliteCount > 0
    ? observation.satelliteCount
    : null;
  let quality = "metadata_limited";
  if (!observation || observation.outOfOrder) quality = "unreliable_observation";
  else if (accuracyMeters !== null && satelliteCount !== null) {
    if (accuracyMeters <= 25 && satelliteCount >= 4) quality = "reported_good";
    else if (accuracyMeters <= 100 && satelliteCount >= 3) quality = "reported_fair";
    else quality = "reported_limited";
  }
  return Object.freeze({ accuracyMeters, satelliteCount, quality });
}

export function qualityForObservation(observation) {
  return metadataForObservation(observation).quality;
}

export function frameAt(geometry, requestedTimestampMs) {
  const observations = geometry?.observations || [];
  if (!observations.length) return null;
  const startMs = observations[0].recordedAt.getTime();
  const endMs = observations.at(-1).recordedAt.getTime();
  const timestampMs = clamp(Number(requestedTimestampMs), startMs, endMs);
  const index = observationIndexAt(observations, timestampMs);
  const observation = observations[index];
  const next = observations[index + 1] || null;
  const edgeClassification = next
    ? geometry.edges?.[index]?.classification ||
      (geometry.reliableEdges[index] ? EDGE_CLASSIFICATIONS.RELIABLE_MOVEMENT : EDGE_CLASSIFICATIONS.MOVEMENT_GAP)
    : null;
  const edgeReliable = edgeClassification === EDGE_CLASSIFICATIONS.RELIABLE_MOVEMENT;
  let coordinates = geometry.matchedObservationCoordinates?.[index] || observation.coordinates;
  let interpolated = false;
  if (edgeReliable && timestampMs > observation.recordedAt.getTime()) {
    const duration = next.recordedAt.getTime() - observation.recordedAt.getTime();
    const ratio = duration > 0 ? (timestampMs - observation.recordedAt.getTime()) / duration : 0;
    coordinates = coordinateOnMatchedPath(geometry.matchedEdgePaths?.[index], timestampMs) ||
      coordinateAt(observation.coordinates, next.coordinates, clamp(ratio, 0, 1));
    interpolated = ratio > 0 && ratio < 1;
  }
  return Object.freeze({
    timestamp: new Date(timestampMs),
    timestampMs,
    observationIndex: index,
    observation,
    nextObservation: next,
    coordinates,
    interpolated,
    edgeClassification,
    inGap: Boolean(next && edgeClassification === EDGE_CLASSIFICATIONS.MOVEMENT_GAP &&
      timestampMs > observation.recordedAt.getTime()),
    inDwell: Boolean(next && edgeClassification === EDGE_CLASSIFICATIONS.STATIONARY_DWELL &&
      timestampMs > observation.recordedAt.getTime()),
    inInvalidEdge: Boolean(next && edgeClassification === EDGE_CLASSIFICATIONS.INVALID &&
      timestampMs > observation.recordedAt.getTime()),
    movementState: observation.speedKph > 0 ? "moving" : "parked",
    quality: qualityForObservation(observation),
    observationMetadata: metadataForObservation(observation),
    progress: endMs === startMs ? 1 : (timestampMs - startMs) / (endMs - startMs),
  });
}

function coordinatePair(value) {
  return [value.latitude, value.longitude];
}

export function partitionSegmentsAt(geometry, frame) {
  if (!frame) return Object.freeze({ completed: Object.freeze([]), remaining: Object.freeze([]) });
  const completed = [];
  const remaining = [];
  for (const segment of geometry.segments) {
    const firstMs = segment.points[0].recordedAt.getTime();
    const lastMs = segment.points.at(-1).recordedAt.getTime();
    if (frame.timestampMs >= lastMs) {
      if (segment.points.length > 1) completed.push(segment.points.map((point) => coordinatePair(point.coordinates)));
      continue;
    }
    if (frame.timestampMs <= firstMs) {
      if (segment.points.length > 1) remaining.push(segment.points.map((point) => coordinatePair(point.coordinates)));
      continue;
    }
    const before = segment.points.filter((point) => point.recordedAt.getTime() <= frame.timestampMs);
    const after = segment.points.filter((point) => point.recordedAt.getTime() > frame.timestampMs);
    const current = coordinatePair(frame.coordinates);
    const traveled = [...before.map((point) => coordinatePair(point.coordinates)), current];
    const ahead = [current, ...after.map((point) => coordinatePair(point.coordinates))];
    if (traveled.length > 1) completed.push(traveled);
    if (ahead.length > 1) remaining.push(ahead);
  }
  return Object.freeze({ completed: Object.freeze(completed), remaining: Object.freeze(remaining) });
}
